/**
 * Free Shipping Progress - Auto-add/remove free gift based on threshold
 * When a tier with reward type "free gift" is qualified, adds that product to the cart.
 * When threshold is no longer met, removes the free gift from the cart.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

const GIFT_PROPERTY_KEY = '_boosterFreeGift';
const GIFT_PROPERTY_VALUE = '1';
const CART_UPDATE_RETRY_DELAYS_MS = [450, 1200];

let isModifyingGift = false;
let pendingSyncAfterModify = false;
let runAddGiftsScheduled = false;
let modifyWatchdog = 0;
/** @type {number[]} */
let retryTimers = [];
/** @type {Map<string, number>} */
const recentlyAddedGifts = new Map();
/** @type {{ variantId: string, thresholdCents: number }[] | null} */
let cachedGiftConfig = null;

/**
 * Resolve `/cart.js` with locale/markets prefix (`Shopify.routes.root`), matching `_upsell-product.liquid`.
 * `Theme.routes.cart_url` is the HTML cart page — never fetch it expecting JSON.
 */
function getCartJsUrl() {
  const root = window.Shopify?.routes?.root;
  if (typeof root === 'string' && root.length > 0) {
    return `${root.endsWith('/') ? root : `${root}/`}cart.js`;
  }
  const base = window.Theme?.routes?.cart_url;
  if (typeof base === 'string' && base.length > 0) {
    return base.replace(/\/?$/, '') + '.js';
  }
  return '/cart.js';
}

/** Liquid exposes `/cart/change`; Cart AJAX expects `.js`. Theme often mirrors `cart_add_url`. */
function getCartChangeJsUrl() {
  const raw = window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

function getCartAddJsUrl() {
  const raw = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

function getCartUpdateJsUrl() {
  const raw = window.Theme?.routes?.cart_update_url ?? '/cart/update.js';
  const url = String(raw).trim();
  return url.includes('.js') ? url : url.replace(/\/?$/, '') + '.js';
}

function withCacheBust(url) {
  const join = url.includes('?') ? '&' : '?';
  return `${url}${join}_=${Date.now()}`;
}

/**
 * Wrong endpoints and password storefronts return HTML; avoid SyntaxError from `.json()`.
 * @param {Response} response
 * @param {string} label
 */
async function readCartApiJson(response, label) {
  const text = await response.text();
  const trimmed = text.trimStart();
  if (trimmed.startsWith('<')) {
    console.warn(
      `[free-shipping-progress] ${label}: expected JSON, got HTML (status ${response.status}).`,
      trimmed.slice(0, 160)
    );
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    console.warn(`[free-shipping-progress] ${label}: JSON parse failed`, e);
    return null;
  }
}

async function fetchCart() {
  const cartRes = await fetch(withCacheBust(getCartJsUrl()), {
    headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    cache: 'no-store',
  });
  return readCartApiJson(cartRes, 'GET cart.js');
}

function parseGiftConfigFromElement(progressEl) {
  const giftVariantIdsStr = progressEl.dataset.giftVariantIds;
  const giftThresholdsStr = progressEl.dataset.giftThresholds;
  if (!giftVariantIdsStr || !giftThresholdsStr) return null;

  const variantIds = giftVariantIdsStr.split(',').map((id) => id.trim()).filter(Boolean);
  const thresholdParts = giftThresholdsStr.split(',').map((t) => parseInt(t.trim(), 10) || 0);
  if (variantIds.length === 0 || variantIds.length !== thresholdParts.length) return null;

  return variantIds.map((vid, i) => ({
    variantId: vid,
    thresholdCents: thresholdParts[i] || 0,
  }));
}

function getGiftConfig() {
  const progressEls = document.querySelectorAll(
    '[data-free-shipping-gift-config][data-gift-variant-ids], .free-shipping-progress[data-gift-variant-ids]'
  );
  for (const el of progressEls) {
    const config = parseGiftConfigFromElement(el);
    if (config) {
      cachedGiftConfig = config;
      return config;
    }
  }
  return cachedGiftConfig;
}

function getSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

function isCartBusy() {
  if (document.querySelector('cart-items-component.cart-items-disabled')) return true;
  // Quantity debounce window: input is locked but fetch has not started yet.
  if (document.querySelector('cart-items-component input[data-pending-quantity]')) return true;
  return false;
}

/**
 * @param {object} item
 */
function getItemProperties(item) {
  return item?.properties && typeof item.properties === 'object' ? item.properties : {};
}

/**
 * Identify theme-managed free gifts. Prefer the marker property so paid units of the
 * same variant are never removed or treated as the gift.
 * @param {object} item
 * @param {string} [variantId]
 */
function isManagedGiftLine(item, variantId) {
  if (variantId != null && String(item.variant_id) !== String(variantId)) return false;
  const props = getItemProperties(item);
  if (String(props[GIFT_PROPERTY_KEY]) === GIFT_PROPERTY_VALUE) return true;
  // Legacy gifts added before the property marker (fully free, qty 1).
  if (variantId == null) return false;
  return item.quantity === 1 && (item.final_line_price === 0 || item.line_price === 0);
}

/**
 * After a free-gift add, Shopify discount recalculation can split the same paid variant
 * into multiple lines (same unit price, different discount allocations). Merge those back.
 * @param {object[]} cartItems
 * @returns {Promise<boolean>}
 */
async function mergeDuplicatePaidLines(cartItems) {
  /** @type {Map<string, object[]>} */
  const groups = new Map();

  for (const item of cartItems) {
    if (isManagedGiftLine(item)) continue;
    if ((item.final_line_price || 0) <= 0) continue;

    const vid = String(item.variant_id);
    if (!groups.has(vid)) groups.set(vid, []);
    groups.get(vid)?.push(item);
  }

  /** @type {Record<string, number>} */
  const updates = {};
  let needsUpdate = false;

  for (const lines of groups.values()) {
    if (lines.length < 2) continue;

    lines.sort((a, b) => b.quantity - a.quantity);
    const keep = lines[0];
    const totalQty = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);

    updates[keep.key] = totalQty;
    if (keep.quantity !== totalQty) needsUpdate = true;

    for (let i = 1; i < lines.length; i++) {
      updates[lines[i].key] = 0;
      needsUpdate = true;
    }
  }

  if (!needsUpdate) return false;

  const response = await fetch(
    getCartUpdateJsUrl(),
    fetchConfig('json', { body: JSON.stringify({ updates }) })
  );
  const data = (await readCartApiJson(response, 'POST cart/update.js')) ?? {};
  if (data.errors) {
    console.warn('Free gift paid-line merge:', data.errors);
    return false;
  }
  return true;
}

/**
 * Re-render cart sections from the live cart.
 * `sectionRenderer.renderSection` aborts in-flight morphs from the original add-to-cart
 * (HTML captured before the gift existed) so they cannot overwrite the drawer.
 * @param {string[]} sectionIds
 * @returns {Promise<Record<string, string>>}
 */
async function refreshCartSections(sectionIds) {
  const sections = {};
  await Promise.all(
    sectionIds.map(async (id) => {
      try {
        sections[id] = await sectionRenderer.renderSection(id, { cache: false });
      } catch (err) {
        console.warn('[free-shipping-progress] section refresh failed:', id, err);
      }
    })
  );
  return sections;
}

/**
 * @param {object | null} cart
 * @param {string} sectionId
 * @param {Record<string, string>} sections
 */
function publishCartUpdate(cart, sectionId, sections) {
  document.dispatchEvent(
    new CartUpdateEvent(cart || {}, sectionId, {
      source: 'free-shipping-progress',
      itemCount: cart?.item_count ?? 0,
      sections,
    })
  );
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function wasRecentlyAdded(vid) {
  const at = recentlyAddedGifts.get(vid);
  if (!at) return false;
  if (Date.now() - at < 5000) return true;
  recentlyAddedGifts.delete(vid);
  return false;
}

/**
 * Wait until quantity updates finish so we don't race line numbers / morphs.
 */
async function waitForCartIdle() {
  for (let i = 0; i < 20; i++) {
    if (!isCartBusy()) return true;
    await wait(100);
  }
  return !isCartBusy();
}

/**
 * @param {string} vid
 * @returns {Promise<object>}
 */
async function addGiftVariant(vid) {
  let lastError = new Error('Failed to add');
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const formData = new FormData();
      formData.append('id', vid);
      formData.append('quantity', '1');
      formData.append(`properties[${GIFT_PROPERTY_KEY}]`, GIFT_PROPERTY_VALUE);

      const response = await fetch(getCartAddJsUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
        keepalive: true,
        cache: 'no-store',
      });
      const data = (await readCartApiJson(response, 'POST cart/add.js')) ?? {};
      if (data.status || !response.ok) {
        throw new Error(data.message || `Failed to add (${response.status})`);
      }
      return data;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt >= 3) break;
      await wait(250 * attempt);
    }
  }
  throw lastError;
}

/**
 * @param {object} item
 * @param {number} quantity
 */
async function changeGiftLineQuantity(item, quantity) {
  const body = JSON.stringify({
    id: item.key,
    quantity,
  });
  const response = await fetch(getCartChangeJsUrl(), fetchConfig('json', { body }));
  return (await readCartApiJson(response, 'POST cart/change.js')) ?? {};
}

async function syncFreeGifts() {
  const giftConfig = getGiftConfig();
  if (!giftConfig) return;

  if (isCartBusy()) {
    pendingSyncAfterModify = true;
    return;
  }

  const sectionIds = getSectionIds();
  const sectionId =
    document.querySelector('.free-shipping-progress[data-gift-variant-ids]')?.closest('cart-items-component')
      ?.dataset?.sectionId || sectionIds[0];

  if (isModifyingGift) {
    pendingSyncAfterModify = true;
    return;
  }

  isModifyingGift = true;
  window.clearTimeout(modifyWatchdog);
  modifyWatchdog = window.setTimeout(() => {
    isModifyingGift = false;
  }, 12000);

  try {
    const idle = await waitForCartIdle();
    // Re-check: a quantity click may have started during the wait.
    if (!idle || isCartBusy()) {
      pendingSyncAfterModify = true;
      return;
    }

    const cart = await fetchCart();
    if (!cart || typeof cart !== 'object') return;
    // Exclude free gift lines from threshold so adding/removing the gift does not oscillate.
    const cartItems = cart.items || [];
    const giftVariantSet = new Set(giftConfig.map((g) => g.variantId));
    const currentTotal = cartItems.reduce((sum, item) => {
      if (giftVariantSet.has(String(item.variant_id)) && isManagedGiftLine(item, String(item.variant_id))) {
        return sum;
      }
      return sum + (item.final_line_price || 0);
    }, 0);

    const toRemove = [];
    const toNormalize = [];

    for (const item of cartItems) {
      const vid = String(item.variant_id);
      const config = giftConfig.find((g) => g.variantId === vid);
      if (!config || !isManagedGiftLine(item, vid)) continue;

      if (config.thresholdCents > 0 && currentTotal < config.thresholdCents) {
        toRemove.push(item);
      } else if (item.quantity > 1) {
        toNormalize.push(item);
      }
    }

    let changed = false;

    if (toRemove.length > 0) {
      for (const item of toRemove) {
        const data = await changeGiftLineQuantity(item, 0);
        if (data.errors) {
          console.warn('Free gift remove:', data.errors);
          continue;
        }
        recentlyAddedGifts.delete(String(item.variant_id));
        changed = true;
      }
    }

    for (const item of toNormalize) {
      const data = await changeGiftLineQuantity(item, 1);
      if (data.errors) {
        console.warn('Free gift normalize:', data.errors);
        continue;
      }
      changed = true;
    }

    const cartAfterRemoves = changed ? await fetchCart() : cart;
    if (!cartAfterRemoves || typeof cartAfterRemoves !== 'object') return;
    const itemsAfter = cartAfterRemoves.items || [];

    const toAdd = giftConfig.filter((g) => {
      if (g.thresholdCents <= 0 || currentTotal < g.thresholdCents) return false;
      if (wasRecentlyAdded(g.variantId)) return false;
      return !itemsAfter.some((item) => isManagedGiftLine(item, g.variantId));
    });

    for (const { variantId: vid } of toAdd) {
      try {
        await addGiftVariant(vid);
        recentlyAddedGifts.set(vid, Date.now());
        changed = true;
      } catch (err) {
        console.warn('Free gift add:', err);
      }
    }

    // Gift cart/add makes Shopify re-run automatic discounts, which can split paid lines.
    let cartForMerge = changed ? await fetchCart() : cartAfterRemoves;
    if (cartForMerge?.items?.length) {
      const merged = await mergeDuplicatePaidLines(cartForMerge.items);
      if (merged) {
        changed = true;
        cartForMerge = await fetchCart();
      }
    }

    if (!changed || !sectionId) return;

    const updatedCart = cartForMerge?.items ? cartForMerge : await fetchCart();
    const sections = await refreshCartSections(sectionIds);
    publishCartUpdate(updatedCart, sectionId, sections);
  } catch (err) {
    console.error('Free shipping progress gift sync error:', err);
  } finally {
    window.clearTimeout(modifyWatchdog);
    isModifyingGift = false;
    if (pendingSyncAfterModify) {
      pendingSyncAfterModify = false;
      runSyncGifts();
    }
  }
}

function runSyncGifts() {
  if (runAddGiftsScheduled) return;
  runAddGiftsScheduled = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      runAddGiftsScheduled = false;
      syncFreeGifts();
    });
  });
}

function clearRetryTimers() {
  retryTimers.forEach((id) => window.clearTimeout(id));
  retryTimers = [];
}

function scheduleSyncRetries() {
  clearRetryTimers();
  // Defer past quantity-change morph so we never race line updates.
  retryTimers.push(window.setTimeout(() => runSyncGifts(), 150));
  for (const ms of CART_UPDATE_RETRY_DELAYS_MS) {
    retryTimers.push(window.setTimeout(() => runSyncGifts(), ms));
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === 'free-shipping-progress') return;
  scheduleSyncRetries();
});

// Quantity changes must finish before gift add/remove so the gift never receives the paid qty.
document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !target.closest('cart-items-component')) return;
  pendingSyncAfterModify = true;
});

document.addEventListener(DialogOpenEvent.eventName, runSyncGifts);

window.addEventListener('pageshow', (event) => {
  if (event.persisted) runSyncGifts();
});

customElements.whenDefined('cart-items-component').then(() => {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer) {
    const dialog = cartDrawer.querySelector('dialog');
    if (dialog) {
      dialog.addEventListener('open', runSyncGifts);
    }
  }

  runSyncGifts();
});
